import React, { useMemo } from "react";
import { GridLegacy as Grid, useTheme, useMediaQuery, Box } from "@mui/material";
import { VList } from "virtua";
import DriverCard, { IDriverMember } from "./card/card";
import DriverCardSkeleton from "./card/cardSkeleton";
import EmptyState from "./EmptyState";

// we don't need to stagger after a certain point due to virtualisation
const MAX_STAGGER_INDEX = 20;

interface DriverGridProps {
  drivers: IDriverMember[];
  isLoading: boolean;
  onStatusChange: (uuid: string, newStatus: "on-shift" | "on-break" | "off-shift") => void;
  onTaskChange: (uuid: string, newTask: string) => void;
  onViewTimeline: (uuid: string) => void;
  canEdit: boolean;
  tasks: Array<{ id: number; key: string; title: string }>;
  pendingDriverUuids: Set<string>;
  hasAnimated: boolean;
}

function useColumnCount(): number {
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up("lg"));
  const isMd = useMediaQuery(theme.breakpoints.up("md"));
  const isSm = useMediaQuery(theme.breakpoints.up("sm"));
  return isLg ? 4 : isMd ? 3 : isSm ? 2 : 1;
}

function DriverGrid({
  drivers,
  isLoading,
  onStatusChange,
  onTaskChange,
  onViewTimeline,
  canEdit,
  tasks,
  pendingDriverUuids,
  hasAnimated,
}: DriverGridProps) {
  const columns = useColumnCount();

  // VList requires a fixed height viewport and the no of rows to compute how many items to virtualise
  const rows = useMemo(() => {
    const result: IDriverMember[][] = [];
    for (let i = 0; i < drivers.length; i += columns) {
      result.push(drivers.slice(i, i + columns));
    }
    return result;
  }, [drivers, columns]);

  if (isLoading) {
    return (
      <Grid container spacing={2} data-testid="driver-list-loading-skeleton">
        {Array.from({ length: 12 }).map((_, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={`skeleton-${index}`}>
            <DriverCardSkeleton animationDelay={index * 40} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (drivers.length === 0) {
    return <EmptyState />;
  }

  return (
    <VList style={{ height: "calc(100vh - 340px)" }}>
      {rows.map((row, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: 2,
            paddingBottom: 2,
          }}
        >
          {row.map((driver) => {
            const flatIndex = rowIndex * columns + row.indexOf(driver);
            return (
              <DriverCard
                key={driver.uuid}
                driver={driver}
                onStatusChange={onStatusChange}
                onTaskChange={onTaskChange}
                onViewTimeline={onViewTimeline}
                animationIndex={!hasAnimated ? Math.min(flatIndex, MAX_STAGGER_INDEX) : undefined}
                canEdit={canEdit}
                tasks={tasks}
                isPending={pendingDriverUuids.has(driver.uuid)}
              />
            );
          })}
        </Box>
      ))}
    </VList>
  );
}

export default React.memo(DriverGrid);
